import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const slides = [
  {
    bg: 'from-primary-700 via-primary-600 to-purple-600',
    badge: '🛍️ New Arrivals 2026',
    title: 'Shop the Best\nDeals Today',
    desc: 'Discover thousands of products at unbeatable prices from top sellers.',
    cta: 'Shop Now',
    ctaLink: '/products',
    emoji: '🛒',
    accent: 'text-primary-200',
  },
  {
    bg: 'from-orange-500 to-pink-500',
    badge: '⚡ Flash Sale',
    title: 'Up to 50% Off\nElectronics',
    desc: 'Limited time deals on the latest gadgets and tech accessories.',
    cta: 'Grab Deals',
    ctaLink: '/products',
    emoji: '📱',
    accent: 'text-orange-200',
  },
  {
    bg: 'from-teal-500 to-cyan-500',
    badge: '👗 New Collection',
    title: 'Fashion Trends\nFor Everyone',
    desc: 'Latest styles for every occasion — delivered fast to your door.',
    cta: 'Explore Fashion',
    ctaLink: '/products',
    emoji: '👠',
    accent: 'text-teal-200',
  },
  {
    bg: 'from-rose-500 to-orange-400',
    badge: '🏠 Home & Living',
    title: 'Transform Your\nLiving Space',
    desc: 'Premium home decor and furniture at prices you will love.',
    cta: 'Shop Home',
    ctaLink: '/products',
    emoji: '🛋️',
    accent: 'text-rose-200',
  },
];

const HomeBannerSlider = () => {
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => setCurrent((c) => (c + 1) % slides.length), []);
  const prev = () => setCurrent((c) => (c - 1 + slides.length) % slides.length);

  useEffect(() => {
    const timer = setInterval(next, 4500);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <div className="relative w-full h-[420px] overflow-hidden">
      {slides.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 bg-gradient-to-r ${slide.bg} text-white transition-opacity duration-700 ${i === current ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
        >
          {/* Decorative circles */}
          <div className="absolute -right-16 -top-16 w-80 h-80 bg-white/10 rounded-full pointer-events-none" />
          <div className="absolute -right-8 -bottom-20 w-56 h-56 bg-white/5 rounded-full pointer-events-none" />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full pointer-events-none" />

          {/* Content */}
          <div className="relative z-10 h-full flex items-center justify-between max-w-6xl mx-auto px-16">
            <div className="space-y-4 max-w-xl">
              <span className={`inline-block bg-white/20 text-sm font-semibold px-4 py-1.5 rounded-full`}>
                {slide.badge}
              </span>
              <h1 className="text-4xl md:text-5xl font-extrabold leading-tight whitespace-pre-line">
                {slide.title}
              </h1>
              <p className={`${slide.accent} text-base max-w-md`}>{slide.desc}</p>
              <div className="flex items-center gap-3 pt-2">
                <Link
                  to={slide.ctaLink}
                  className="inline-flex items-center gap-2 bg-white text-gray-900 font-bold text-sm px-7 py-3 rounded-full hover:bg-gray-100 transition-colors no-underline"
                >
                  {slide.cta}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
                <Link
                  to="/seller-guide"
                  className="inline-block text-white/80 hover:text-white text-sm font-medium no-underline transition-colors"
                >
                  Become a Seller →
                </Link>
              </div>
            </div>
            <div className="hidden md:block text-[120px] select-none opacity-90 drop-shadow-lg">
              {slide.emoji}
            </div>
          </div>
        </div>
      ))}

      {/* Left arrow */}
      <button
        onClick={prev}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/20 hover:bg-white/35 text-white flex items-center justify-center transition-colors"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Right arrow */}
      <button
        onClick={next}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/20 hover:bg-white/35 text-white flex items-center justify-center transition-colors"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Dots */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`h-2 rounded-full transition-all duration-300 ${i === current ? 'w-8 bg-white' : 'w-2 bg-white/40 hover:bg-white/60'}`}
          />
        ))}
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 z-20 h-0.5 bg-white/20">
        <div
          key={current}
          className="h-full bg-white/60 animate-[progress_4.5s_linear_forwards]"
          style={{ animation: 'slideProgress 4.5s linear forwards' }}
        />
      </div>
    </div>
  );
};

export default HomeBannerSlider;
